/*
 * This file is part of glowingblue/orell-facade.
 *
 * Copyright (c) 2022 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { slug } from '../../../common';
import Component from 'flarum/common/Component';
import Setup from './FiledGroups/Setup';
import Content from './FiledGroups/Content';
import Visual from './FiledGroups/Visual';
import Link from './FiledGroups/Link';
import GA from './FiledGroups/GA';
import BannerFromState from '../../states/BannerFromState';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import BannerPreview from './BannerPreview';

const t = app.translator.trans.bind(app.translator);
const prfx = `${slug}.forum.banner_form`;

const FORM_FIELD_GROUPS = {
	setup: Setup,
	content: Content,
	visual: Visual,
	link: Link,
	ga: GA,
};

export default class BannerForm extends Component {
	oninit(vnode) {
		super.oninit(vnode);

		const { banner } = this.attrs;

		this.state = new BannerFromState(banner);
		app.bannerFormState = this.state;

		this.deleting = false;
	}

	view() {
		const { banner } = this.attrs;

		return (
			<div className="BannerForm">
				<form onsubmit={this.onsubmit.bind(this)}>
					{Object.keys(FORM_FIELD_GROUPS).map((key) => {
						const FieldGroup = FORM_FIELD_GROUPS[key];
						return <FieldGroup groupKey={key} />;
					})}

					<div className="Form-group BannerForm-submit">
						<Button type="submit" className="Button Button--primary" icon="fas fa-save" loading={this.state.loading}>
							{t(`${prfx}.submit`)}
						</Button>

						{banner.exists && (
							<Button className="Button Button--secondary" icon="fas fa-trash-alt" loading={this.deleting} onclick={this.delete.bind(this)}>
								{t(`${prfx}.delete`)}
							</Button>
						)}
					</div>
				</form>

				<BannerPreview />
			</div>
		);
	}

	onsubmit(e) {
		e.preventDefault();

		this.state
			.save()
			.then(() => {
				// Show success alert
				const alertId = app.alerts.show(
					{
						type: 'success',
						controls: [
							<Button
								className="Button Button--link"
								onclick={() =>
									m.route.set(
										app.route('compose-banner', {
											edit: this.state.banner.id(),
										})
									)
								}
							>
								{t(`${prfx}.continue_editing`)}
							</Button>,
						],
					},
					t(`${prfx}.success`)
				);

				// Hide alert after 10 seconds
				setTimeout(() => app.alerts.dismiss(alertId), 10000);

				// Redirect to banner list
				m.route.set(app.route('facade-manager'));
			})
			.catch(() => {
				this.state.loading = false;
				m.redraw();

				// Show error alert
				app.alerts.show({ type: 'error' }, t(`${prfx}.error`));
			});
	}

	delete() {
		if (!confirm(t(`${prfx}.delete_confirm`))) {
			return;
		}

		const { banner } = this.attrs;

		this.deleting = true;
		m.redraw();

		banner
			.delete()
			.then(() => {
				this.deleting = false;
				m.redraw();

				// Show success alert
				const alertId = app.alerts.show({ type: 'success' }, t(`${prfx}.delete_success`));

				// Hide alert after 10 seconds
				setTimeout(() => app.alerts.dismiss(alertId), 10000);

				// Redirect to banner list
				m.route.set(app.route('facade-manager'));
			})
			.catch(() => {
				this.deleting = false;
				m.redraw();

				// Show error alert
				app.alerts.show({ type: 'error' }, t(`${prfx}.delete_error`));
			});
	}
}
